import { Inject, Injectable } from '@angular/core';
import { Actions, OnInitEffects, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { ActivityFilter } from '@shared/models/filter.model';
import { ActivitiesSearchService } from '@shared/services/search/activities-search.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { WINDOW } from '@shared/shared/tokens/window';
import { ACTIVITIES_STATE } from 'app.config';
import { catchError, combineLatest, filter, map, of, switchMap, take, tap } from 'rxjs';
import * as fromActions from '../actions/travel-activities-search.actions';
import { ActivitiesState } from '../reducers/activities.reducer';
import { getActivitiesSearchFeatureState, getSelectableActivitiesPrice, getSortedActivitiesPrices } from '../selectors';

interface StoredActivitiesState {
  paging: ActivitiesState['paging'];
  sort: ActivitiesState['sort'];
  filterState: ActivitiesState['filterState'];
}

@Injectable()
export class ActivitiesSearchEffects implements OnInitEffects {
  constructor(
    private actions$: Actions,
    private siteSettingsService: SiteSettingsService,
    private activitiesSearchService: ActivitiesSearchService,
    private store: Store,
    @Inject(WINDOW) private window: Window
  ) {}

  public ngrxOnInitEffects(): Action {
    return new fromActions.RestoreActivitiesState();
  }

  public getActivitiesSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_ACTIVITIES_SEARCH_RESULTS),
      switchMap((action: fromActions.GetActivitiesSearchResults) => {
        return this.siteSettingsService.fetchSiteSettings().pipe(
          take(1),
          switchMap((siteSettings) =>
            this.activitiesSearchService.search(action.form, siteSettings.language, siteSettings.currency).pipe(
              map((data) => new fromActions.GetActivitiesSearchResultsSuccess(data)),
              catchError(() => of(new fromActions.GetActivitiesSearchResultsFailure()))
            )
          )
        );
      })
    )
  );

  public applyActivitiesFilterOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_ACTIVITIES_SEARCH_RESULTS_SUCCESS),
      map(() => new fromActions.ApplyActivitiesFilter())
    )
  );

  public changeActivitiesFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.ACTIVITIES_PRICE_FILTER_CHANGE),
      switchMap((action: fromActions.ActivitiesPriceFilterChange) =>
        combineLatest([
          this.store.select(getSortedActivitiesPrices),
          this.store.select(getSelectableActivitiesPrice),
        ]).pipe(
          take(1),
          map(([sortedPrices, selectablePrice]) => {
            const minPrice = action.price[0];
            const maxPrice =
              action.price[1] < selectablePrice[1] ? action.price[1] : sortedPrices[sortedPrices.length - 1];

            return new fromActions.SetActivitiesFilter({ price: [minPrice, maxPrice] });
          })
        )
      )
    )
  );

  public getActivitiesCategoryFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_ACTIVITIES_CATEGORIES),
      switchMap((action: fromActions.GetActivitiesCategories) =>
        this.siteSettingsService.fetchSiteSettings().pipe(
          take(1),
          switchMap((siteSettings) =>
            this.activitiesSearchService.categories(siteSettings.language, action.locationId).pipe(
              map((categories) => {
                const categoryFilters: ActivityFilter<number>[] = categories.map((category) => ({
                  value: category.attributeId,
                  name: category.attributeName,
                  enabled: true,
                  selected: false,
                }));
                return new fromActions.GetActivitiesCategoriesSuccess(categoryFilters, action.keepSelected);
              }),
              catchError(() => of(new fromActions.GetActivitiesCategoriesFailure()))
            )
          )
        )
      )
    )
  );

  public storeActivitiesState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.SET_ACTIVITIES_PAGING, fromActions.SET_ACTIVITIES_SORT, fromActions.APPLY_ACTIVITIES_FILTER),
        switchMap(() => this.store.select(getActivitiesSearchFeatureState).pipe(take(1))),
        tap((state) => {
          const storedState: StoredActivitiesState = {
            paging: state.paging,
            sort: state.sort,
            filterState: state.filterState,
          };
          this.window.localStorage.setItem(ACTIVITIES_STATE, JSON.stringify(storedState));
        })
      ),
    { dispatch: false }
  );

  public restoreActivitiesState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.RESTORE_ACTIVITIES_STATE),
      map(() => JSON.parse(this.window.localStorage.getItem(ACTIVITIES_STATE))),
      filter((rawState) => !!rawState),
      switchMap((rawState: StoredActivitiesState) => {
        const actions = [];

        if (rawState.paging) {
          actions.push(new fromActions.SetActivitiesPaging(rawState.paging));
        }

        if (rawState.sort) {
          actions.push(new fromActions.SetActivitiesSort(rawState.sort));
        }

        if (rawState.filterState) {
          actions.push(new fromActions.SetActivitiesFilter(rawState.filterState));
          actions.push(new fromActions.ApplyActivitiesFilter());
        }

        return of(...actions);
      })
    )
  );
}
